import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { sportsCategories } from '@/mocks/common';
import { featuredEvents } from '@/mocks/home';

const categoryLabels: Record<string, string> = {
  football: '足球',
  basketball: '篮球',
  tennis: '网球',
  esports: '电竞',
  baseball: '棒球',
  other: '综合',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'football';
  const currentCategory = sportsCategories.find((c) => c.slug === currentSlug);
  const categoryName = currentCategory?.name || categoryLabels[currentSlug] || currentSlug;

  const filteredEvents = currentSlug === 'sports'
    ? featuredEvents
    : featuredEvents.filter((e) => e.category === currentSlug);

  const displayEvents = filteredEvents.length > 0 ? filteredEvents : featuredEvents;

  return (
    <>
      <SEOHead
        title={`${categoryName}赛事 - 开元体育`}
        description={`开元体育${categoryName}赛事专区，提供${categoryName}实时赔率、赛程安排和专业分析。覆盖全球顶级${categoryName}赛事，安全公平流畅的在线体育体验。`}
        keywords={`开元体育,${categoryName},${categoryName}赛事,${categoryName}赔率,开元体育${categoryName}`}
        canonicalUrl={`https://www.kaiyuansports.com/category/${currentSlug}`}
      />

      <div className="pt-20 pb-14 md:pb-20 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { name: '首页', href: '/' },
                { name: '赛事中心', href: '/category/sports' },
                { name: categoryName },
              ]}
            />

            <div className="mt-6">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                {categoryName}赛事
              </h1>
              <p className="text-foreground-500 text-sm mt-2">
                {currentCategory?.description || `开元体育${categoryName}赛事专区，全球顶级赛事全覆盖`}
              </p>
            </div>

            <div className="mt-8 flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
              {sportsCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    currentSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayEvents.map((event) => (
                <div
                  key={event.id}
                  className="bg-background-50 border border-background-200 rounded-lg p-5 hover:border-primary-300 transition-all duration-200 cursor-pointer group"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium text-foreground-500 bg-background-100 px-2 py-0.5 rounded">
                      {categoryLabels[event.category] || event.category}
                    </span>
                    <div className="flex items-center gap-1.5">
                      {event.status === 'live' && (
                        <span className="flex items-center gap-1 text-accent-500 text-xs font-semibold">
                          <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse"></span>
                          进行中
                        </span>
                      )}
                      {event.status === 'upcoming' && (
                        <span className="text-foreground-400 text-xs">即将开始</span>
                      )}
                    </div>
                  </div>

                  <p className="text-xs text-foreground-400 mb-3">{event.league}</p>

                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1 text-center">
                      <p className="font-semibold text-foreground-900 text-sm whitespace-nowrap">{event.homeTeam}</p>
                      {event.homeScore !== null && (
                        <p className="text-2xl font-heading font-bold text-foreground-950 mt-1">{event.homeScore}</p>
                      )}
                    </div>
                    <div className="px-3">
                      <span className="text-xs font-semibold text-foreground-400">VS</span>
                    </div>
                    <div className="flex-1 text-center">
                      <p className="font-semibold text-foreground-900 text-sm whitespace-nowrap">{event.awayTeam}</p>
                      {event.awayScore !== null && (
                        <p className="text-2xl font-heading font-bold text-foreground-950 mt-1">{event.awayScore}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-3 border-t border-background-200">
                    {event.drawOdds ? (
                      <>
                        <div className="flex-1 text-center py-1.5 rounded bg-background-100 group-hover:bg-primary-50 transition-colors">
                          <p className="text-xs text-foreground-500">主胜</p>
                          <p className="text-sm font-bold text-primary-600">{event.homeOdds}</p>
                        </div>
                        <div className="flex-1 text-center py-1.5 rounded bg-background-100 group-hover:bg-primary-50 transition-colors">
                          <p className="text-xs text-foreground-500">平局</p>
                          <p className="text-sm font-bold text-foreground-700">{event.drawOdds}</p>
                        </div>
                        <div className="flex-1 text-center py-1.5 rounded bg-background-100 group-hover:bg-primary-50 transition-colors">
                          <p className="text-xs text-foreground-500">客胜</p>
                          <p className="text-sm font-bold text-primary-600">{event.awayOdds}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex-1 text-center py-1.5 rounded bg-background-100 group-hover:bg-primary-50 transition-colors">
                          <p className="text-xs text-foreground-500">主胜</p>
                          <p className="text-sm font-bold text-primary-600">{event.homeOdds}</p>
                        </div>
                        <div className="flex-1 text-center py-1.5 rounded bg-background-100 group-hover:bg-primary-50 transition-colors">
                          <p className="text-xs text-foreground-500">客胜</p>
                          <p className="text-sm font-bold text-primary-600">{event.awayOdds}</p>
                        </div>
                      </>
                    )}
                  </div>

                  <p className="text-xs text-foreground-400 mt-3 text-center">
                    <i className="ri-time-line mr-1"></i>
                    {event.time}
                  </p>
                </div>
              ))}
            </div>

            {displayEvents.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-calendar-todo-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">暂无{categoryName}赛事数据</p>
                <p className="text-foreground-400 text-sm mt-1">请稍后再来查看</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}